// SCR-STAFF-996 — مدیریت پروفایل غنی پزشکان (US-CHN-070..072؛ پیش‌نویس، انتشار، QR، ظرفیت).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, LoadingState, PageHeader } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

export const SCR_STAFF_996 = 'SCR-STAFF-996';

interface ProfileRow {
  providerId: string;
  displayName: string;
  hasPendingDraft: boolean;
  slug?: string | null;
  showRemainingCapacity: boolean;
  publishedVersion: number;
  draft?: { slug?: string | null; showRemainingCapacity?: boolean; education?: { degree: string; institution: string; year?: number | null }[] } | null;
  rich?: { slug?: string | null; showRemainingCapacity?: boolean; education?: { degree: string; institution: string; year?: number | null }[] } | null;
}

type TabId = 'overview' | 'history' | 'slug';

export function ProviderPresencePage() {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('chn.public_page.manage');
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('overview');
  const [slug, setSlug] = useState('');
  const [education, setEducation] = useState('');
  const [showCapacity, setShowCapacity] = useState(false);

  const list = useQuery({
    queryKey: ['chn', session?.tenantId, 'provider-presence'],
    queryFn: async () => {
      const body = await unwrap(tenantApi.GET('/api/v1/channels/public-page/providers'));
      return (body as { items: ProfileRow[] }).items;
    },
  });

  const current = useMemo(() => list.data?.find((row) => row.providerId === selected) ?? list.data?.[0] ?? null, [list.data, selected]);
  const providerId = current?.providerId;

  const saveDraft = useMutation({
    mutationFn: async () => {
      if (!providerId) {
        throw new Error('provider');
      }
      const content = {
        education: education.trim()
          ? education
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean)
              .map((line) => {
                const [degree, institution, year] = line.split('|').map((part) => part.trim());
                return { degree: degree ?? '', institution: institution ?? '', year: year ? Number(year) : null };
              })
          : [],
        experience: [],
        awards: [],
        socialLinks: [],
        acceptedInsurerCodes: [],
        acceptedContractIds: [],
        mapUrl: null,
        showRemainingCapacity: showCapacity,
        slug: slug.trim() || null,
      };
      return unwrap(
        tenantApi.PUT('/api/v1/channels/public-page/providers/{providerId}/draft' as never, {
          params: { path: { providerId } },
          body: { content },
        } as never),
      );
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['chn', session?.tenantId, 'provider-presence'] }),
  });

  const publish = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/channels/public-page/providers/{providerId}:publish' as never, {
          params: { path: { providerId } },
        } as never),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['chn', session?.tenantId, 'provider-presence'] }),
  });

  const reject = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/channels/public-page/providers/{providerId}:reject' as never, {
          params: { path: { providerId } },
        } as never),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['chn', session?.tenantId, 'provider-presence'] }),
  });

  if (list.error) {
    return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  }
  if (!list.data) {
    return <LoadingState rows={4} />;
  }

  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_996} data-testid="scr-staff-996">
      <PageHeader titleKey="chn:presence.title" subtitleKey="chn:presence.subtitle" />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {list.data.map((row) => (
          <Chip
            key={row.providerId}
            label={`${row.displayName}${row.hasPendingDraft ? ' *' : ''}`}
            color={row.providerId === providerId ? 'primary' : 'default'}
            onClick={() => {
              setSelected(row.providerId);
              setSlug(row.draft?.slug ?? row.slug ?? '');
              setShowCapacity(row.draft?.showRemainingCapacity ?? row.showRemainingCapacity);
              setEducation((row.draft?.education ?? row.rich?.education ?? []).map((item) => `${item.degree}|${item.institution}|${item.year ?? ''}`).join('\n'));
            }}
            data-testid={`presence-provider-${row.providerId}`}
          />
        ))}
      </Stack>
      {!current ? <Alert severity="info">{t('chn:presence.empty')}</Alert> : null}
      {current ? (
        <>
          <Tabs value={tab} onChange={(_, value: TabId) => setTab(value)}>
            <Tab value="overview" label={t('chn:presence.tab_overview')} />
            <Tab value="history" label={t('chn:presence.tab_history')} />
            <Tab value="slug" label={t('chn:presence.tab_slug')} />
          </Tabs>
          {tab === 'overview' ? (
            <Stack spacing={2}>
              <Typography variant="body2">
                {t('chn:presence.draft_status')}: {current.hasPendingDraft ? t('chn:presence.pending') : t('chn:presence.published')} (v{current.publishedVersion})
              </Typography>
              <FormControlLabel
                control={<Switch checked={showCapacity} onChange={(_, checked) => setShowCapacity(checked)} disabled={!write} />}
                label={t('chn:presence.show_capacity')}
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={!write || saveDraft.isPending} onClick={() => saveDraft.mutate()} data-testid="presence-save-draft">
                  {t('chn:presence.save_draft')}
                </Button>
                <Button variant="outlined" disabled={!write || !current.hasPendingDraft || publish.isPending} onClick={() => publish.mutate()} data-testid="presence-publish">
                  {t('chn:presence.publish')}
                </Button>
                <Button disabled={!write || !current.hasPendingDraft || reject.isPending} onClick={() => reject.mutate()} data-testid="presence-reject">
                  {t('chn:presence.reject')}
                </Button>
              </Stack>
            </Stack>
          ) : null}
          {tab === 'history' ? (
            <TextField
              label={t('chn:presence.education')}
              helperText={t('chn:presence.education_hint')}
              value={education}
              onChange={(event) => setEducation(event.target.value)}
              multiline
              minRows={4}
              disabled={!write}
              fullWidth
            />
          ) : null}
          {tab === 'slug' ? (
            <Stack spacing={2}>
              <TextField label={t('chn:presence.slug')} value={slug} onChange={(event) => setSlug(event.target.value)} disabled={!write} fullWidth inputProps={{ 'data-testid': 'presence-slug' }} />
              <Button
                variant="outlined"
                href={`/api/v1/channels/provider-profiles/${current.providerId}/qr?format=svg`}
                target="_blank"
                rel="noreferrer"
                data-testid="presence-qr"
              >
                {t('chn:presence.download_qr')}
              </Button>
            </Stack>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
